import { useCallback, useEffect, useState } from "react";

// Define the form data structure
export interface SigningConfig {
  bundleName?: string;
  encodeIds: boolean;
  patchIds: boolean;
  forceOriginalId: boolean;
  patchDebug: boolean;
  patchAllDevices: boolean;
  patchFileSharing: boolean;
  patchGameMode: boolean;
  patchPromotion: boolean;
  patchFullscreen: boolean;
  patchOrientation: boolean;
  patchLiquidGlass: boolean;
  removeUrlSchemes: boolean;
  patchStatusBar: "default" | "hidden" | "light" | "dark";
  patchUserInterfaceStyle: "default" | "light" | "dark";
  injectWarpsignFix: boolean;
  hideHomeIndicator: boolean;
  certificateType: "distribution" | "development";
}

// Default form values
const defaultConfig: SigningConfig = {
  bundleName: "",
  encodeIds: true,
  patchIds: true,
  forceOriginalId: false,
  patchDebug: false,
  patchAllDevices: false,
  patchFileSharing: false,
  patchGameMode: false,
  patchPromotion: false,
  patchFullscreen: false,
  patchOrientation: false,
  patchLiquidGlass: false,
  removeUrlSchemes: false,
  patchStatusBar: "default",
  patchUserInterfaceStyle: "default",
  injectWarpsignFix: false,
  hideHomeIndicator: false,
  certificateType: "distribution",
};

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        ready: () => void;
        expand: () => void;
        sendData: (data: string) => void;
        close: () => void;
        MainButton: {
          setText: (text: string) => void;
          show: () => void;
          hide: () => void;
          enable: () => void;
          disable: () => void;
          onClick: (callback: () => void) => void;
          offClick: (callback: () => void) => void;
        };
        BackButton: {
          show: () => void;
          hide: () => void;
          onClick: (callback: () => void) => void;
          offClick: (callback: () => void) => void;
        };
        onEvent: (eventType: string, callback: () => void) => void;
        offEvent: (eventType: string, callback: () => void) => void;
      };
    };
  }
}

export const useSign = () => {
  const [config, setConfig] = useState<SigningConfig>(defaultConfig);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Initialize Telegram WebApp
  useEffect(() => {
    const initTelegramWebApp = () => {
      if (window.Telegram?.WebApp) {
        const webApp = window.Telegram.WebApp;

        // Initialize the WebApp
        webApp.ready();
        webApp.expand();

        // Show back button
        webApp.BackButton.show();

        // Handle back button clicks
        const handleBackButton = () => {
          webApp.close();
        };

        webApp.BackButton.onClick(handleBackButton);
        webApp.onEvent("backButtonClicked", handleBackButton);

        setIsReady(true);

        // Cleanup function
        return () => {
          webApp.BackButton.offClick(handleBackButton);
          webApp.offEvent("backButtonClicked", handleBackButton);
        };
      }
    };

    // Check if Telegram WebApp is already loaded
    if (window.Telegram?.WebApp) {
      initTelegramWebApp();
    } else {
      // Wait for Telegram WebApp to load
      const checkTelegram = setInterval(() => {
        if (window.Telegram?.WebApp) {
          initTelegramWebApp();
          clearInterval(checkTelegram);
        }
      }, 100);

      // Fallback for non-Telegram environments (development/testing)
      const fallbackTimeout = setTimeout(() => {
        if (!window.Telegram?.WebApp) {
          console.warn(
            "Telegram WebApp not available, enabling fallback mode for development"
          );
          setIsReady(true);
        }
        clearInterval(checkTelegram);
      }, 1000);

      return () => {
        clearInterval(checkTelegram);
        clearTimeout(fallbackTimeout);
      };
    }
  }, []);

  // Update individual config values
  const updateConfig = useCallback((key: keyof SigningConfig, value: any) => {
    setConfig((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Collect form data from DOM elements
  const collectFormData = useCallback((): SigningConfig => {
    const formData: Partial<SigningConfig> = {};

    // Get bundle name from input
    const bundleNameInput = document.querySelector(
      'input[placeholder="Enter app name"]'
    ) as HTMLInputElement;
    if (bundleNameInput?.value) {
      formData.bundleName = bundleNameInput.value;
    }

    // Get radio button values
    const certificateRadio = document.querySelector(
      'input[name="certificate"]:checked'
    ) as HTMLInputElement;
    if (certificateRadio) {
      formData.certificateType = certificateRadio.value as
        | "distribution"
        | "development";
    }

    // Get checkbox values
    const checkboxes = [
      { name: "encode_ids", key: "encodeIds" as keyof SigningConfig },
      { name: "patch_ids", key: "patchIds" as keyof SigningConfig },
      {
        name: "force_original_id",
        key: "forceOriginalId" as keyof SigningConfig,
      },
      { name: "patch_debug", key: "patchDebug" as keyof SigningConfig },
      {
        name: "patch_all_devices",
        key: "patchAllDevices" as keyof SigningConfig,
      },
      {
        name: "patch_file_sharing",
        key: "patchFileSharing" as keyof SigningConfig,
      },
      { name: "patch_game_mode", key: "patchGameMode" as keyof SigningConfig },
      { name: "patch_promotion", key: "patchPromotion" as keyof SigningConfig },
      {
        name: "patch_fullscreen",
        key: "patchFullscreen" as keyof SigningConfig,
      },
      {
        name: "patch_orientation",
        key: "patchOrientation" as keyof SigningConfig,
      },
      {
        name: "patch_liquid_glass",
        key: "patchLiquidGlass" as keyof SigningConfig,
      },
      {
        name: "remove_url_schemes",
        key: "removeUrlSchemes" as keyof SigningConfig,
      },
      {
        name: "inject_warpsign_fix",
        key: "injectWarpsignFix" as keyof SigningConfig,
      },
      {
        name: "hide_home_indicator",
        key: "hideHomeIndicator" as keyof SigningConfig,
      },
    ];

    checkboxes.forEach(({ name, key }) => {
      const checkbox = document.querySelector(
        `input[name="${name}"]`
      ) as HTMLInputElement;
      if (checkbox) {
        (formData as any)[key] = checkbox.checked;
      }
    });

    // Get select values
    const statusBarSelect = document.querySelector(
      'select[name="patch_status_bar"]'
    ) as HTMLSelectElement;
    if (statusBarSelect) {
      formData.patchStatusBar =
        statusBarSelect.value as SigningConfig["patchStatusBar"];
    }

    const interfaceStyleSelect = document.querySelector(
      'select[name="patch_user_interface_style"]'
    ) as HTMLSelectElement;
    if (interfaceStyleSelect) {
      formData.patchUserInterfaceStyle =
        interfaceStyleSelect.value as SigningConfig["patchUserInterfaceStyle"];
    }

    return { ...defaultConfig, ...formData };
  }, []);

  // Send data to Telegram
  const sendToTelegram = useCallback(async () => {
    if (!isReady) {
      console.error("App is not ready");
      return;
    }

    setIsLoading(true);

    try {
      // Collect current form data
      const currentConfig = collectFormData();

      // Prepare data for Telegram
      const telegramData = {
        type: "signing_config",
        config: currentConfig,
      };

      if (window.Telegram?.WebApp) {
        // Send data to the bot if in Telegram environment
        window.Telegram.WebApp.sendData(JSON.stringify(telegramData));
        // Close the WebApp
        window.Telegram.WebApp.close();
      } else {
        // Fallback for development/testing - just log the data
        console.log("Development mode: Would send to Telegram:", telegramData);
        alert("Development mode: Check console for signing configuration data");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error sending data to Telegram:", error);
      setIsLoading(false);
    }
  }, [isReady, collectFormData]);

  // Handle form submission
  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      if (event) {
        event.preventDefault();
      }
      sendToTelegram();
    },
    [sendToTelegram]
  );

  return {
    config,
    updateConfig,
    handleSubmit,
    isLoading,
    isReady,
    collectFormData,
  };
};
